import type { HistoryEntry, Moved, PublicState, Target } from "../protocol";
import { targetAnchor } from "./game-text";
import { tableScale } from "./table-scale";

export type Dealt = { source: number; to: Target; amount: number; combat: boolean };

export type Strike = Dealt & { from: DOMRect | null; at: DOMRect | null };

export type Floater = { id: number; x: number; y: number; text: string; tone: "damage" | "loss" | "gain"; delay: number };

export const LUNGE_MS = 520;
export const IMPACT_AT = 0.55;
export const IMPACT_MS = Math.round(LUNGE_MS * IMPACT_AT);
const MOST_NEW_ENTRIES = 40;

export function newDamage(before: HistoryEntry[], after: HistoryEntry[]): Dealt[] {
  if (before.length === 0 || after.length <= before.length || after.length - before.length > MOST_NEW_ENTRIES) return [];
  return after.slice(before.length).flatMap((e) => e.happened.flatMap((h) =>
    h.kind === "DealtDamage" && h.values[3] > 0 ? [{ source: h.values[0], to: h.values[2], amount: h.values[3], combat: h.values[4] }] : []));
}

function anchored(root: ParentNode, key: string): HTMLElement | null {
  return root.querySelector<HTMLElement>(`[data-anchor~="${CSS.escape(key)}"]`);
}

export function measureStrikes(root: ParentNode, dealt: Dealt[]): Strike[] {
  return dealt.map((d) => ({
    ...d,
    from: anchored(root, `obj:${d.source}`)?.getBoundingClientRect() ?? null,
    at: anchored(root, targetAnchor(d.to))?.getBoundingClientRect() ?? null,
  }));
}

function becameOf(moves: Moved[], oid: number): number {
  let now = oid;
  for (const m of moves) if (m.from_object === now) now = m.to_object;
  return now;
}

function permanentNow(root: ParentNode, moves: Moved[], oid: number): HTMLElement | null {
  const node = anchored(root, `obj:${becameOf(moves, oid)}`);
  return node?.matches(".perm") ? node : null;
}

function nodeNow(root: ParentNode, moves: Moved[], t: Target): HTMLElement | null {
  if (t.kind === "TargetPlayer") return anchored(root, `seat:${t.values[0]}`);
  return permanentNow(root, moves, t.values[0]);
}

function centre(r: DOMRect): { x: number; y: number } {
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

export function reducedMotion(): boolean {
  return matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function playStrikes(root: ParentNode, strikes: Strike[], moves: Moved[]): Animation[] {
  if (reducedMotion()) return [];
  const out: Animation[] = [];
  const k = tableScale(root);
  const lunged = new Set<number>();
  const shaken = new Set<string>();
  for (const s of strikes) {
    if (s.combat && s.from && s.at && !lunged.has(s.source)) {
      const node = permanentNow(root, moves, s.source);
      if (node) {
        lunged.add(s.source);
        const a = centre(s.from);
        const b = centre(s.at);
        const dx = (b.x - a.x) / k;
        const dy = (b.y - a.y) / k;
        const len = Math.hypot(dx, dy) || 1;
        const reach = Math.max(0, len - (s.at.height + s.from.height) * 0.3 / k) / len;
        out.push(node.animate([
          { translate: "0px 0px", offset: 0 },
          { translate: `${(-dx / len) * 10}px ${(-dy / len) * 10}px`, offset: 0.2, easing: "cubic-bezier(0.5, 0, 0.9, 0.4)" },
          { translate: `${dx * reach}px ${dy * reach}px`, offset: IMPACT_AT, easing: "cubic-bezier(0.2, 0.7, 0.3, 1)" },
          { translate: "0px 0px", offset: 1 },
        ], { duration: LUNGE_MS, composite: "add" }));
        out.push(node.animate([{ zIndex: 60 }, { zIndex: 60 }], { duration: LUNGE_MS }));
      }
    }
    const key = targetAnchor(s.to);
    if (shaken.has(key)) continue;
    const hit = nodeNow(root, moves, s.to);
    if (!hit) continue;
    shaken.add(key);
    const delay = s.combat ? IMPACT_MS : 0;
    out.push(hit.animate([
      { translate: "0px 0px" }, { translate: "-7px 2px" }, { translate: "6px -2px" }, { translate: "-4px 1px" }, { translate: "2px 0px" }, { translate: "0px 0px" },
    ], { duration: 320, delay, composite: "add" }));
    out.push(hit.animate([
      { filter: "brightness(1.9) sepia(1) hue-rotate(-50deg) saturate(4)" }, { filter: "none" },
    ], { duration: 360, delay, easing: "ease-out" }));
  }
  return out;
}

let floaterIds = 0;

export function damageFloaters(strikes: Strike[], origin: DOMRect, k: number): Floater[] {
  const totals = new Map<number, { amount: number; at: DOMRect; combat: boolean }>();
  for (const s of strikes) {
    if (s.to.kind !== "TargetObject" || !s.at) continue;
    const t = totals.get(s.to.values[0]);
    totals.set(s.to.values[0], { amount: (t?.amount ?? 0) + s.amount, at: s.at, combat: s.combat || (t?.combat ?? false) });
  }
  return [...totals.values()].map((t) => ({
    id: ++floaterIds, x: (t.at.left - origin.left + t.at.width / 2) / k, y: (t.at.top - origin.top + t.at.height / 2) / k,
    text: `−${t.amount}`, tone: "damage", delay: t.combat ? IMPACT_MS : 0,
  }));
}

export function lifeChanges(before: PublicState, after: PublicState): { pid: string; delta: number }[] {
  return Object.values(after.players).flatMap((p) => {
    const was = before.players[p.id];
    return was && was.life !== p.life ? [{ pid: p.id, delta: p.life - was.life }] : [];
  });
}

export function lifeFloaters(root: ParentNode, changes: { pid: string; delta: number }[], origin: DOMRect, k: number, delay: number): Floater[] {
  return changes.flatMap(({ pid, delta }) => {
    const plate = root.querySelector(`[data-anchor~="${CSS.escape(`seat:${pid}`)}"] .life-plate`);
    if (!plate) return [];
    const r = plate.getBoundingClientRect();
    return [{
      id: ++floaterIds, x: (r.left - origin.left + r.width / 2) / k, y: (r.top - origin.top) / k - 18,
      text: delta > 0 ? `+${delta}` : `−${-delta}`, tone: delta > 0 ? "gain" : "loss", delay,
    } satisfies Floater];
  });
}

export function pulseLife(root: ParentNode, changes: { pid: string; delta: number }[], delay: number): Animation[] {
  if (reducedMotion()) return [];
  return changes.flatMap(({ pid, delta }) => {
    const plate = root.querySelector<HTMLElement>(`[data-anchor~="${CSS.escape(`seat:${pid}`)}"] .life-plate`);
    if (!plate) return [];
    const glow = delta > 0 ? "rgb(62 196 109 / 0.95)" : "rgb(229 72 77 / 0.95)";
    return [plate.animate([
      { scale: "1", filter: "none" },
      { scale: "1.28", filter: `drop-shadow(0 0 10px ${glow})`, offset: 0.25 },
      { scale: "1", filter: "none" },
    ], { duration: 700, delay, easing: "ease-out" })];
  });
}
